import {
  EncounterArrivalMode,
  EncounterCreate,
  EncounterRegistrationDetails,
  EncounterRegistrationType,
  EncounterStatus,
} from "@/types/emr/encounter/encounter";

export type SpecializedRegistrationDraft = {
  patientId: string;
  facilityId: string;
  organizationId: string;
  registrationType: EncounterRegistrationType;
  details: EncounterRegistrationDetails;
};

const encounterClassByRegistration = {
  emergency: "emer",
  day_care: "obsenc",
  newborn: "obsenc",
  health_checkup: "amb",
} as const;

const statusByRegistration = {
  emergency: EncounterStatus.IN_PROGRESS,
  day_care: EncounterStatus.PLANNED,
  newborn: EncounterStatus.IN_PROGRESS,
  health_checkup: EncounterStatus.PLANNED,
} as const;

const priorityByRegistration = {
  emergency: "urgent",
  day_care: "elective",
  newborn: "routine",
  health_checkup: "routine",
} as const;

const startFieldByRegistration = {
  emergency: "incident_datetime",
  day_care: "scheduled_start",
  newborn: "birth_datetime",
  health_checkup: "visit_datetime",
} as const;

const endFieldByRegistration = {
  emergency: undefined,
  day_care: "expected_discharge",
  newborn: undefined,
  health_checkup: undefined,
} as const;

export function buildSpecializedEncounter(
  draft: SpecializedRegistrationDraft,
): EncounterCreate {
  const startField = startFieldByRegistration[draft.registrationType];
  const endField = endFieldByRegistration[draft.registrationType];
  const start = draft.details[startField];
  const end = endField ? draft.details[endField] : undefined;

  return {
    patient: draft.patientId,
    facility: draft.facilityId,
    organizations: [draft.organizationId],
    primary_organization: draft.organizationId,
    status: statusByRegistration[draft.registrationType],
    encounter_class: encounterClassByRegistration[draft.registrationType],
    priority: priorityByRegistration[draft.registrationType],
    period: {
      start: typeof start === "string" ? start : new Date().toISOString(),
      end: typeof end === "string" ? end : undefined,
    },
    hospitalization:
      draft.registrationType === "emergency"
        ? {
            arrival_mode:
              typeof draft.details.arrival_mode === "string"
                ? (draft.details.arrival_mode as EncounterArrivalMode)
                : undefined,
            arrival_time:
              typeof draft.details.incident_datetime === "string"
                ? draft.details.incident_datetime
                : undefined,
          }
        : {},
    registration_type: draft.registrationType,
    registration_details: draft.details,
    tags: [],
  };
}

export function joinLocalDateTime(date?: string, time?: string) {
  if (!date || !time) return undefined;
  const value = new Date(`${date}T${time}`);
  return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
}
